// Duck AI Analysis Service
// Frontend service for on-demand AI-powered signal analysis
// Uses Gemini API for professional market commentary

const GEMINI_API_KEY = import.meta.env.VITE_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

export interface SignalData {
    type: 'BUY' | 'SELL' | 'HOLD';
    confidence: number;
    price: number;
    reason: string;
    rsi?: number;
    macd?: string;
    trend?: string;
    change?: string;
    marketData?: {
        volume24h: number;
        liquidity: number;
        marketCap: number;
    };
    tokenSymbol?: string;
}

export interface AIAnalysisResult {
    commentary: string;
    educationalInsight: string;
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
    keyPoints: string[];
    actionSuggestion: string;
    disclaimer: string;
}

/**
 * Generate professional AI commentary for a trading signal
 * Called on-demand when user clicks "Analyze with Duck AI" button
 */
export async function analyzeSignalWithAI(signal: SignalData, token: any): Promise<AIAnalysisResult | null> {
    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not configured');
        return null;
    }

    const symbol = token?.symbol || signal.tokenSymbol || 'TOKEN';

    const prompt = `You are Duck AI, a professional cryptocurrency market analyst specializing in meme coins on the Monad blockchain. Analyze this trading signal for ${symbol} and provide expert, educational commentary.

SIGNAL DATA:
- Signal Type: ${signal.type}
- Confidence: ${signal.confidence}%
- Current Price: ${signal.price.toFixed(8)}
- Technical Reason: ${signal.reason}
${signal.rsi ? `- RSI: ${signal.rsi}` : ''}
${signal.macd ? `- MACD: ${signal.macd}` : ''}
${signal.trend ? `- Trend: ${signal.trend}` : ''}
${signal.change ? `- 24h Change: ${signal.change}` : ''}
${signal.marketData ? `- 24h Volume: $${signal.marketData.volume24h.toLocaleString()}` : ''}
${signal.marketData ? `- Liquidity: $${signal.marketData.liquidity.toLocaleString()}` : ''}
${signal.marketData ? `- Market Cap: $${signal.marketData.marketCap.toLocaleString()}` : ''}

TASK: Provide a JSON response with professional analysis in ENGLISH:
{
    "commentary": "Deep dive analysis (2-3 sentences). Explain WHY this signal matters given the indicators and market data. Mention specific numbers.",
    "educationalInsight": "Explain a technical concept used in this analysis (e.g., 'What is RSI Divergence?', 'Why is volume important?'). 1-2 sentences.",
    "riskLevel": "LOW" or "MEDIUM" or "HIGH",
    "keyPoints": ["key point 1", "key point 2", "key point 3"],
    "actionSuggestion": "Short, clear action recommendation",
    "disclaimer": "Brief investment warning"
}

IMPORTANT:
- Use professional but accessible language
- Explain technical terms simply in the 'educationalInsight' field
- Be clear about risk level
- Respond in ENGLISH only
- Return only JSON format`;

    try {
        const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{
                    parts: [{ text: prompt }]
                }],
                generationConfig: {
                    temperature: 0.7,
                    maxOutputTokens: 1024,
                }
            }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text;

        if (!text) {
            throw new Error('No response from AI');
        }

        // Extract JSON from response
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            return {
                commentary: parsed.commentary || 'Analysis complete.',
                educationalInsight: parsed.educationalInsight || 'Always check market conditions before trading.',
                riskLevel: parsed.riskLevel || 'MEDIUM',
                keyPoints: parsed.keyPoints || [],
                actionSuggestion: parsed.actionSuggestion || 'Proceed with caution.',
                disclaimer: parsed.disclaimer || 'Not financial advice.',
            };
        }

        return null;
    } catch (error) {
        console.error('AI analysis failed:', error);
        return null;
    }
}

/**
 * Check if AI service is available
 */
export function isAIAvailable(): boolean {
    return !!GEMINI_API_KEY;
}
